import { useCallback, useEffect, useRef, useState } from "react";

type TriggerMode = 'onScroll' | 'manual';

interface UseCountUpOptions {
  end: number;
  duration?: number;
  decimals?: number;
  triggerMode: TriggerMode;
  isVisible?: boolean; 
}

export const useCounter = (options: UseCountUpOptions) => {
  const { 
    end, 
    duration = 2000, 
    decimals = 0, 
    triggerMode, 
    isVisible = false 
  } = options;
  
  const [count, setCount] = useState(0);
  const hasAnimatedRef = useRef(false);
  const ref = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  const animateCount = useCallback(() => {
    if (hasAnimatedRef.current && triggerMode === 'onScroll') return;
    
    if (triggerMode === 'onScroll') {
      hasAnimatedRef.current = true;
    }
    
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    const startTime = Date.now();
    const startValue = 0;
    setCount(0); 

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = startValue + (end - startValue) * easeOutQuart;

      const finalCount = parseFloat(currentCount.toFixed(decimals));
      
      setCount(finalCount);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setCount(end);
        animationFrameRef.current = null;
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [decimals, duration, end, triggerMode]);

  useEffect(() => {
    let observer: IntersectionObserver | undefined;
    let scheduledFrame: number | null = null;

    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = undefined;
    }

    if (triggerMode === 'manual') {
      if (isVisible) {
        scheduledFrame = requestAnimationFrame(() => {
          cleanupRef.current = animateCount();
        });
      }
    } 
    else if (triggerMode === 'onScroll' && ref.current) {
      const observedElement = ref.current;
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !hasAnimatedRef.current) {
            cleanupRef.current = animateCount();
            observer?.unobserve(observedElement);
          }
        },
        { threshold: 0.3 }
      );
      observer.observe(observedElement);
    }

    return () => {
      if (scheduledFrame !== null) {
        cancelAnimationFrame(scheduledFrame);
        scheduledFrame = null;
      }
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = undefined;
      }
      if (observer) {
        observer.disconnect();
      }
    };

  }, [animateCount, isVisible, triggerMode]);

  return { count, ref };
};
